import React, { useState } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { DEFAULT_PAGE_SIZE } from '../constants';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  render: (item: T) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyField: keyof T;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  pageSize?: number;
  emptyMessage?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  testIdPrefix?: string;
}

function DataTable<T extends Record<string, any>>({
  data,
  columns,
  keyField,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  pageSize = DEFAULT_PAGE_SIZE,
  emptyMessage = 'No data found',
  sortField,
  sortDirection = 'asc',
  onSort,
  testIdPrefix = 'table',
}: DataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(data.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const pageData = data.slice(startIndex, startIndex + pageSize);

  const allPageSelected =
    pageData.length > 0 && pageData.every((item) => selectedIds.has(String(item[keyField])));
  const somePageSelected = pageData.some((item) => selectedIds.has(String(item[keyField])));

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    const newIds = new Set(selectedIds);
    if (allPageSelected) {
      pageData.forEach((item) => newIds.delete(String(item[keyField])));
    } else {
      pageData.forEach((item) => newIds.add(String(item[keyField])));
    }
    onSelectionChange(newIds);
  };

  const handleSelectRow = (id: string) => {
    if (!onSelectionChange) return;
    const newIds = new Set(selectedIds);
    if (newIds.has(id)) {
      newIds.delete(id);
    } else {
      newIds.add(id);
    }
    onSelectionChange(newIds);
  };

  const handleSort = (field: string) => {
    if (onSort) onSort(field);
  };

  // Reset page if data shrinks
  const safePage = Math.min(currentPage, Math.max(1, totalPages));
  if (safePage !== currentPage) setCurrentPage(safePage);

  if (data.length === 0) {
    return (
      <div
        className="text-center py-12 bg-white rounded-lg shadow"
        data-testid={`${testIdPrefix}-empty`}
      >
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div
      className="bg-white shadow rounded-lg overflow-hidden"
      data-testid={`${testIdPrefix}-container`}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {selectable && (
                <th scope="col" className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    data-testid={`${testIdPrefix}-select-all`}
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected && !allPageSelected;
                    }}
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    col.sortable ? 'cursor-pointer select-none hover:text-gray-700' : ''
                  } ${col.className || ''}`}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.sortable &&
                      sortField === col.key &&
                      (sortDirection === 'asc' ? (
                        <ChevronUpIcon className="h-3 w-3" />
                      ) : (
                        <ChevronDownIcon className="h-3 w-3" />
                      ))}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pageData.map((item) => {
              const id = String(item[keyField]);
              const isSelected = selectedIds.has(id);
              return (
                <tr
                  key={id}
                  data-testid={`${testIdPrefix}-row-${id}`}
                  // `group` enables Tailwind's `group-hover:*` modifier on
                  // descendant elements so cells can reveal row-level
                  // affordances (e.g. inline action buttons) on hover.
                  className={`group ${
                    isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                  } transition-colors`}
                >
                  {selectable && (
                    <td className="w-12 px-4 py-3">
                      <input
                        type="checkbox"
                        data-testid={`${testIdPrefix}-select-${id}`}
                        checked={isSelected}
                        onChange={() => handleSelectRow(id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 py-3 text-sm ${col.className || ''}`}>
                      {col.render(item)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-sm text-gray-500">
            Showing {startIndex + 1}-{Math.min(startIndex + pageSize, data.length)} of {data.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid={`${testIdPrefix}-prev-page`}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              data-testid={`${testIdPrefix}-next-page`}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
